/** @format */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, Info, HelpCircle } from "lucide-react";
import useDebounce from "../../hooks/useDebounce";
import { customizeProduct } from "../../actions/blindActions";
import { shadeTypeDefaults } from "./data";
import Customize_Reworked from ".";

const dimensionRegex = /^\d+(?:\.\d+|\s+\d+\/\d+)?$/;

const validateDimension = (value) => {
  if (!value || value.trim() === "") return "This field is required";
  if (!dimensionRegex.test(value.trim()))
    return "Enter a valid value (e.g., 22, 22.5, or 22 1/2)";
  return null;
};

const sanitizeDimension = (value) => {
  let v = value.replace(/[^\d./\s]/g, "");
  v = v.replace(/\s+/g, " ");
  return v;
};

const normalizeDimension = (value) => {
  let v = value.trim();
  const match = v.match(/^(\d+)\s+(\d+)\/(\d+)$/);
  if (match) {
    const [, whole, num, denom] = match;
    v = parseFloat(
      (parseInt(whole) + parseInt(num) / parseInt(denom)).toFixed(2),
    ).toString();
  }
  return v;
};

const Customize_Details = () => {
  // --- Redux and Router Hooks ---
  const dispatch = useDispatch();
  const location = useLocation();

  // --- Redux State ---
  const { productInfo = {}, optionInfo = {} } = useSelector(
    (state) => state?.customize?.customizeProduct || {},
  );

  // --- Component State ---
  const [shadeWidth, setShadeWidth] = useState(optionInfo?.widthWhole || "");
  const [shadeLength, setShadeLength] = useState(optionInfo?.lengthWhole || "");
  const [widthError, setWidthError] = useState(null);
  const [lengthError, setLengthError] = useState(null);
  const [measure, setMeasure] = useState(optionInfo?.measure || "Tight");
  const [stackType, setStackType] = useState(optionInfo?.stackType || "Left");

  const isVerticalSheer = productInfo?.Type === "Vertical Sheer";

  // --- Debounced Values ---
  const debouncedWidth = useDebounce(shadeWidth, 300);
  const debouncedLength = useDebounce(shadeLength, 300);

  // --- URL Params ---
  const [params, setParams] = useSearchParams();
  const defaultConfig = params.get("default");
  const fabricGroup = params.get("fabric");

  // Dispatch updates when debounced dimensions or other selections change
  useEffect(() => {
    if (!defaultConfig) {
      const wErr = validateDimension(debouncedWidth);
      const lErr = validateDimension(debouncedLength);
      setWidthError(wErr);
      setLengthError(lErr);
      if (!wErr && !lErr) {
        dispatchDetails();
      }
    }
  }, [debouncedWidth, debouncedLength, measure, stackType, defaultConfig]);

  // Handle loading default configuration from URL parameter
  useEffect(() => {
    if (defaultConfig && shadeTypeDefaults[defaultConfig]) {
      const defaults = shadeTypeDefaults[defaultConfig];

      const defaultProductInfo = {
        ...defaults.product,
        Fabric_Type: fabricGroup || defaults.product?.Fabric_Type || "All",
        Default_Fabric_Type:
          fabricGroup || defaults.product?.Default_Fabric_Type || "All",
      };

      dispatch(
        customizeProduct({
          productInfo: defaultProductInfo,
          optionInfo: defaults.option,
        }),
      );

      setShadeWidth(defaults.option.widthWhole);
      setShadeLength(defaults.option.lengthWhole);
      setMeasure(defaults.option.measure);

      setParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        newParams.delete("default");
        return newParams;
      });
    } else if (defaultConfig) {
      console.error(`Invalid default configuration key: ${defaultConfig}`);
    }
  }, [defaultConfig, fabricGroup, dispatch]);

  // --- Dispatch Function ---
  const dispatchDetails = () => {
    if (!productInfo) {
      console.error("Cannot dispatch details: productInfo is missing.");
      return;
    }
    dispatch(
      customizeProduct({
        productInfo: { ...productInfo },
        optionInfo: {
          ...optionInfo,
          widthWhole: shadeWidth,
          lengthWhole: shadeLength,
          measure: measure,
          stackType: stackType,
        },
      }),
    );
  };

  // --- Animation Variants ---
  const inputVariants = {
    focus: {
      scale: 1.01,
      borderColor: "rgb(var(--color-primary) / 0.5)",
      boxShadow: "0 0 0 2px rgb(var(--color-primary) / 0.2)",
    },
    blur: { scale: 1, borderColor: "#d1d5db", boxShadow: "none" },
  };

  // --- Component Render ---
  return (
    <Customize_Reworked>
      <div className="space-y-8">
        {/* --- Size Input Section --- */}
        <fieldset className="space-y-4">
          {/* Section Header */}
          <legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Ruler className="w-5 h-5 text-primary" />
              </div>
              Enter Dimensions (inches)
            </div>
            <Link
              to="/guides/measuring-guide"
              className="text-primary text-xs hover:underline ml-auto inline-flex items-center gap-1 flex-shrink-0"
              target="_blank"
              rel="noopener noreferrer"
            >
              <HelpCircle size={14} /> Need Help Measuring?
            </Link>
          </legend>

          {/* Width & Length Input Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {/* Width Input */}
            <div className="space-y-2">
              <label
                htmlFor="width"
                className="block text-sm font-medium text-gray-700"
              >
                Width
              </label>
              <motion.div
                variants={inputVariants}
                whileFocus="focus"
                animate="blur"
                transition={{ duration: 0.15 }}
                className="relative"
              >
                <input
                  id="width"
                  aria-label="Width"
                  type="text"
                  value={shadeWidth}
                  onChange={(e) =>
                    setShadeWidth(sanitizeDimension(e.target.value))
                  }
                  onBlur={(e) =>
                    setShadeWidth(normalizeDimension(e.target.value))
                  }
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:ring-0 outline-none transition-all duration-150 ${
                    widthError
                      ? "border-red-400 focus:border-red-400"
                      : "border-gray-300 focus:border-primary"
                  }`}
                  placeholder="e.g., 36, 36.5, or 36 1/2"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                  in
                </span>
              </motion.div>
              {widthError && (
                <p className="text-xs text-red-500">{widthError}</p>
              )}
            </div>

            {/* Length Input */}
            <div className="space-y-2">
              <label
                htmlFor="length"
                className="block text-sm font-medium text-gray-700"
              >
                Length
              </label>
              <motion.div
                variants={inputVariants}
                whileFocus="focus"
                animate="blur"
                transition={{ duration: 0.15 }}
                className="relative"
              >
                <input
                  id="length"
                  aria-label="Length"
                  type="text"
                  value={shadeLength}
                  onChange={(e) =>
                    setShadeLength(sanitizeDimension(e.target.value))
                  }
                  onBlur={(e) =>
                    setShadeLength(normalizeDimension(e.target.value))
                  }
                  className={`w-full px-4 py-2.5 bg-gray-50 border rounded-lg focus:ring-0 outline-none transition-all duration-150 ${
                    lengthError
                      ? "border-red-400 focus:border-red-400"
                      : "border-gray-300 focus:border-primary"
                  }`}
                  placeholder="e.g., 48, 48.5, or 48 1/2"
                  required
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                  in
                </span>
              </motion.div>
              {lengthError && (
                <p className="text-xs text-red-500">{lengthError}</p>
              )}
            </div>
          </div>
        </fieldset>
				{isVerticalSheer && (
					<fieldset className="space-y-4 pt-4">
						<legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
							<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
								<Info className="w-5 h-5 text-primary" />
							</div>
							Stack Type
						</legend>
						<select
							id="stack-type"
							aria-label="Stack Type"
							value={stackType}
							onChange={(e) => setStackType(e.target.value)}
							className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none appearance-none transition-all duration-150"
						>
							{["Left", "Right", "Center", "Stack At Will"].map((opt) => (
								<option key={opt} value={opt}>{opt}</option>
							))}
						</select>
					</fieldset>
				)}
        {/* --- Mount Type Section --- */}
        <fieldset className="space-y-4 pt-4">
          {/* Section Header */}
          <legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-primary" />
            </div>
            Select Mount Type
          </legend>
          {/* Mount Info Explanation */}
          <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
            Choose <strong>Inside Mount</strong> to install within the window
            frame (we automatically deduct 1/8" from the width for clearance).
            Choose <strong>Outside Mount</strong> (Finish or Cloth) to install
            outside/above the frame (we make the shade to your exact specified
            width).{" "}
            <Link
              to="/guides/measuring-guide"
              className="text-primary hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more about mount types.
            </Link>
          </p>
          {/* Mount Type Options Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                id: "Tight",
                label: "Inside Mount",
                desc: 'Fits inside frame (1/8" auto-deduction)',
                img: "/images/customizeToolRenders/Inside.png",
              },
              {
                id: "Finish",
                label: "Outside Mount (Finish Size)",
                desc: "Covers frame, shade is exact width",
                img: "/images/customizeToolRenders/Outside.png",
              },
              {
                id: "Cloth",
                label: "Outside Mount (Cloth Size)",
                desc: "Covers frame, fabric is exact width",
                img: "/images/customizeToolRenders/fabric.png",
                hidden: ["Vertical Sheer", "Simply Cellular"].includes(
                  productInfo?.Type,
                ),
              },
            ]
              .filter((type) => !type.hidden)
              .map((type) => (
                <motion.label
                  key={type.id}
                  htmlFor={`mount-${type.id}`}
                  whileHover={{
                    y: -3,
                    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                  }}
                  transition={{ duration: 0.15 }}
                  className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all flex flex-col ${
                    measure === type.id
                      ? "border-primary bg-primary/5 shadow-md"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="aspect-video rounded-md overflow-hidden mb-3 border border-gray-100 bg-gray-50">
                    <img
                      src={type.img}
                      alt={type.label}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex items-start gap-2 mt-auto pt-2">
                    <input
                      id={`mount-${type.id}`}
                      aria-label={type.label}
                      type="radio"
                      name="mount"
                      value={type.id}
                      checked={measure === type.id}
                      onChange={(e) => setMeasure(e.target.value)}
                      className="mt-1 w-4 h-4 text-primary focus:ring-primary/50 focus:ring-offset-1 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <span className="block font-medium text-sm leading-snug">
                        {type.label}
                      </span>
                      <span className="block text-xs text-gray-600 leading-snug">
                        {type.desc}
                      </span>
                    </div>
                  </div>
                </motion.label>
              ))}
          </div>
        </fieldset>
      </div>
    </Customize_Reworked>
  );
};

export default Customize_Details;
